import { Component, OnInit, inject, ChangeDetectorRef, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LaboratoryService } from '../../../../core/services/laboratory.service';
import { Store } from '@ngrx/store';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { LaboratoryOrderModalComponent } from '../laboratory-order-modal/laboratory-order-modal';
import { LaboratoryOrderDetailsComponent } from '../laboratory-order-details/laboratory-order-details';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { toast } from '../../../../shared/utils/toast';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';
import { orderStatusKey } from '../../../../core/utils/order-status.utils';

@Component({
  selector: 'app-laboratory-orders',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    AppPrimeTable, 
    AppDropdown,
    AppDropdown,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ButtonModule,
    DatePickerModule,
    TranslateModule,
    AppButton,
    AppButton,
    LaboratoryOrderModalComponent,
    LaboratoryOrderDetailsComponent,
    ConfirmDialogModule,
    TooltipModule,
    HasPermissionDirective
  ],
  providers: [ConfirmationService],
  templateUrl: './laboratory-orders.html'
})
export class LaboratoryOrdersComponent implements OnInit {
  readonly getOrderStatusKey = orderStatusKey;
  private laboratoryService = inject(LaboratoryService);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private confirmationService = inject(ConfirmationService);
  private perm = inject(PermissionService);

  @ViewChild('statusTpl', { static: true }) statusTpl!: TemplateRef<any>;
  @ViewChild('actionsTpl', { static: true }) actionsTpl!: TemplateRef<any>;

  orders: any[] = [];
  loading = true;
  searchControl = new FormControl('');
  startDateControl = new FormControl<Date | null>(null);
  endDateControl = new FormControl<Date | null>(null);
  branchId: number | null = null;

  // Pagination
  totalRecords = 0;
  rows = 10;
  first = 0;

  // Modal
  showNewOrderModal = false;
  showDetailsModal = false;
  selectedOrder: any = null;
  orderToEdit: any = null;

  templates: { [key: string]: TemplateRef<any> } = {};

  columns: PrimeTableColumn[] = [
    { field: 'laboratory_name', header: 'laboratory', width: '25%' },
    { field: 'patient_name', header: 'patient', width: '25%' },
    { field: 'type_prosthesis', header: 'type', width: '20%' },
    { field: 'start_date', header: 'start_date', width: '10%' },
    { field: 'end_date', header: 'end_date', width: '10%' },
    { field: 'status_name', header: 'status', type: 'custom', width: '5%' },
    { field: 'actions', header: 'actions', type: 'action', width: '5%' }
  ];

  ngOnInit() {
    this.templates = {
      'status_name': this.statusTpl,
      'actions': this.actionsTpl
    };

    this.store.select(selectBranchId).subscribe(id => {
      this.branchId = id;
      if (id) {
        this.loadData();
      }
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.loadData();
    });

    this.startDateControl.valueChanges.subscribe(() => this.loadData());
    this.endDateControl.valueChanges.subscribe(() => this.loadData());
  }

  async loadData() {
    if (!this.branchId) return;
    this.loading = true;
    const search = this.searchControl.value || '';
    
    try {
      const result = await firstValueFrom(
        this.laboratoryService.getOrders(
          this.branchId, 
          search, 
          this.rows, 
          this.first,
          this.startDateControl.value || undefined,
          this.endDateControl.value || undefined
        )
      );
      
      this.orders = result.data;
      this.totalRecords = result.count || 0;
      
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'Confirmado':
        return 'bg-green-100 text-green-700';
      case 'Cancelado':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-amber-100 text-amber-700';
    }
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    this.loadData();
  }

  onNewOrder() {
    if (!this.perm.canCreate(MODULE.LABORATORIOS)) return;
    this.orderToEdit = null; // Clear any previous edit data
    this.showNewOrderModal = true;
  }

  onOrderSaved() {
    this.loadData();
    this.orderToEdit = null;
  }

  onViewDetails(order: any) {
    this.selectedOrder = order;
    this.showDetailsModal = true;
  }

  onStatusChanged() {
    this.loadData();
  }

  onEditOrder(order: any) {
    if (!this.perm.canEdit(MODULE.LABORATORIOS)) return;
    this.orderToEdit = order;
    this.showNewOrderModal = true;
  }

  onDeleteOrder(order: any) {
    if (!this.perm.canDelete(MODULE.LABORATORIOS)) return;
    this.confirmationService.confirm({
      message: this.translate.instant('confirm_delete_order') || 'Are you sure you want to delete this order?',
      header: this.translate.instant('confirm_delete') || 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptLabel: this.translate.instant('delete') || 'Delete',
      rejectLabel: this.translate.instant('common_cancel') || 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        try {
          await firstValueFrom(this.laboratoryService.deleteOrder(order.id));
          toast.success(this.translate.instant('order_deleted_success') || 'Order deleted successfully');
          this.loadData();
        } catch (error) {
          console.error('Error deleting order', error);
          toast.error(this.translate.instant('order_delete_error') || 'Error deleting order');
        }
      }
    });
  }
}
